import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';

const IS_DEV = process.env.NODE_ENV !== 'production';

function formatCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function PaymentModal({ plan, billingPeriod, onClose, onSuccess }) {
  const [paymentMethod, setPaymentMethod] = useState('google_pay');
  const [simulateFailure, setSimulateFailure] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Credit card stub fields (no real validation)
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');

  const amountCents = billingPeriod === 'yearly'
    ? plan.price_yearly_cents
    : plan.price_monthly_cents;

  const handlePayment = async (method, token) => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        planId: plan.id,
        billingPeriod,
        paymentMethod: method,
        paymentToken: token,
      };
      if (IS_DEV && method === 'simulated') {
        payload.simulateSuccess = !simulateFailure;
      }
      const response = await api.post('/subscriptions/subscribe', payload);
      onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGooglePay = async () => {
    if (!window.PaymentRequest) {
      // Fallback: simulate in dev
      if (IS_DEV) {
        await handlePayment('simulated', 'dev_fallback_token');
        return;
      }
      setError('Google Pay is not supported in this browser.');
      return;
    }
    // Stub: in dev, skip the payment sheet
    if (IS_DEV) {
      await handlePayment('google_pay', 'gp_stub_dev_token');
      return;
    }
    // TODO: Real Google Pay — replace test merchant ID with registered merchant ID
    try {
      const request = new window.PaymentRequest(
        [{
          supportedMethods: 'https://google.com/pay',
          data: {
            apiVersion: 2, apiVersionMinor: 0,
            merchantInfo: { merchantId: 'BCR2DN4TXXX', merchantName: 'Brings to Mind' },
            allowedPaymentMethods: [{
              type: 'CARD',
              parameters: { allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'], allowedCardNetworks: ['VISA', 'MASTERCARD'] },
              tokenizationSpecification: { type: 'PAYMENT_GATEWAY', parameters: { gateway: 'example', gatewayMerchantId: 'exampleMerchantId' } },
            }],
          },
        }],
        { total: { label: `Brings to Mind ${plan.name}`, amount: { currency: 'USD', value: (amountCents / 100).toFixed(2) } } }
      );
      const paymentResponse = await request.show();
      const token = JSON.stringify(paymentResponse.details);
      await paymentResponse.complete('success');
      await handlePayment('google_pay', token);
    } catch (err) {
      if (err.name !== 'AbortError') setError('Google Pay failed: ' + err.message);
    }
  };

  const handleApplePay = async () => {
    const isApplePayAvailable = window.ApplePaySession && window.ApplePaySession.canMakePayments();
    if (!isApplePayAvailable) {
      if (IS_DEV) {
        await handlePayment('apple_pay', 'ap_stub_dev_token');
        return;
      }
      setError('Apple Pay is only available in Safari on Apple devices.');
      return;
    }
    // TODO: Replace with domain-verified Apple Pay session
    await handlePayment('apple_pay', 'ap_stub_dev_token');
  };

  const handleCreditCard = async (e) => {
    e.preventDefault();
    // Stub: just send a fake token
    await handlePayment('simulated', `cc_stub_${Date.now()}`);
  };

  const displayAmount = billingPeriod === 'yearly'
    ? `${formatCents(plan.price_yearly_cents)}/year`
    : `${formatCents(plan.price_monthly_cents)}/month`;

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h3>Upgrade to {plan.name}</h3>
          <button onClick={onClose}>&#10005;</button>
        </div>
        <div style={{ padding: '1.5rem' }}>
          <p style={{ marginBottom: '1rem', textAlign: 'center', fontSize: '1.25rem', fontWeight: 'bold' }}>
            {displayAmount}
          </p>

          {/* Payment method tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
            {['google_pay', 'apple_pay', 'credit_card'].map(m => (
              <button
                key={m}
                onClick={() => setPaymentMethod(m)}
                style={{
                  flex: 1, padding: '0.5rem',
                  border: paymentMethod === m ? '2px solid var(--color-primary, #4a90d9)' : '1px solid #ddd',
                  borderRadius: '6px', background: 'none', cursor: 'pointer',
                  fontWeight: paymentMethod === m ? 'bold' : 'normal',
                  fontSize: '0.8em',
                }}
              >
                {m === 'google_pay' ? 'Google Pay' : m === 'apple_pay' ? 'Apple Pay' : 'Credit Card'}
              </button>
            ))}
          </div>

          {/* DEV-only simulation controls */}
          {IS_DEV && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '0.5rem 0.75rem', marginBottom: '1rem', fontSize: '0.8em' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={simulateFailure} onChange={e => setSimulateFailure(e.target.checked)} />
                Dev: Simulate payment failure
              </label>
            </div>
          )}

          {/* Google Pay */}
          {paymentMethod === 'google_pay' && (
            <button
              onClick={handleGooglePay}
              disabled={loading}
              style={{
                width: '100%', padding: '0.75rem',
                background: '#000', color: '#fff',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem',
              }}
            >
              {loading ? 'Processing…' : 'Pay with Google Pay'}
            </button>
          )}

          {/* Apple Pay */}
          {paymentMethod === 'apple_pay' && (
            <button
              onClick={handleApplePay}
              disabled={loading}
              style={{
                width: '100%', padding: '0.75rem',
                background: '#000', color: '#fff',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem',
              }}
            >
              {loading ? 'Processing…' : 'Pay with Apple Pay'}
            </button>
          )}

          {/* Credit Card stub */}
          {paymentMethod === 'credit_card' && (
            <form onSubmit={handleCreditCard} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <input
                placeholder="Card number"
                value={cardNumber}
                onChange={e => setCardNumber(e.target.value)}
                maxLength={19}
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', width: '100%', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  placeholder="MM / YY"
                  value={cardExpiry}
                  onChange={e => setCardExpiry(e.target.value)}
                  maxLength={5}
                  style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', flex: 1 }}
                />
                <input
                  placeholder="CVC"
                  value={cardCvc}
                  onChange={e => setCardCvc(e.target.value)}
                  maxLength={4}
                  style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', flex: 1 }}
                />
              </div>
              <button type="submit" className="btn-save" disabled={loading} style={{ width: '100%' }}>
                {loading ? 'Processing…' : `Pay ${displayAmount}`}
              </button>
            </form>
          )}

          {error && (
            <p style={{ color: 'red', marginTop: '0.75rem', textAlign: 'center', fontSize: '0.875em' }}>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PaymentModal;
